import * as compression from '@codegrade/compression';
import * as deque from '@codegrade/deque';
import { Maybe } from '@codegrade/maybe';
import { eitherAsync, map } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import * as utils from '@/utils';
import * as fileUtils from '@/utils/files';
import * as signals from '@/utils/signal';
import getWsClient from '@/ws_client';
/**
 * By default the max size of 16MiB.
 *
 * This is stored in a mutable object to reduce this size during tests.
 */
export const MAX_CACHE_SIZE = { value: 1 << 24 };
const storeBuilder = getStoreBuilder();
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function makeURLCacheKey(route, hash) {
    return hash.map(h => `H:${h}`).orDefaultLazy(() => `R:${utils.buildUrl(route)}`);
}
/**
 * Makes a hash-basedy cache key with using a JWT as fallback.
 *
 * @param jwt  The token to use in the CacheKey.
 * @param hash A maybe of a hash to use.
 * @returns CacheKey for files that can be retrieved with JWT.
 */
function makeJWTHashKey(jwt, hash) {
    return hash.map(h => `H:${h}`).orDefaultLazy(() => `J:${jwt}`);
}
/** @returns The initial state of the code store. */
function getClearState() {
    return {
        cacheMap: new map.MaybeMap(),
        cacheDeque: new deque.Deque(),
        cacheSize: 0,
        loaders: {},
    };
}
const moduleBuilder = storeBuilder.module('code', getClearState());
/** The code store module. */
export var CodeStore;
(function (CodeStore) {
    const clearCodeCache = moduleBuilder.commit(state => {
        const cleanState = getClearState();
        Object.entries(cleanState).forEach(([key, value]) => {
            utils.vueSet(state, key, value);
        });
    }, 'clearCodeCache');
    signals.AFTER_USER_CHANGED.registerSync(() => {
        clearCodeCache();
    });
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const accessedCode = moduleBuilder.commit((state, { key }) => {
        const deq = state.cacheDeque;
        if (deq.isEmpty()) {
            return;
        }
        const lastVal = deq.peekFront();
        if (lastVal === key) {
            return;
        }
        for (let len = deq.length, oldVal = lastVal, i = 1; i < len; ++i) {
            const prevVal = deq.setAt(i, oldVal);
            if (prevVal === key) {
                // We just overridden the value we want at the beginning, so we
                // set it at index 0 and we are done.
                deq.setAt(0, prevVal);
                break;
            }
            else {
                oldVal = prevVal;
            }
        }
    }, 'accessedCode');
    const addCodeToCache = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, { key, compressed }) => {
        const maxSize = MAX_CACHE_SIZE.value;
        const deq = state.cacheDeque;
        const cacheMap = state.cacheMap;
        if (compressed.compressedByteLength > maxSize) {
            return;
        }
        let cacheSize = state.cacheSize + compressed.compressedByteLength;
        const removedIds = [];
        while (cacheSize > maxSize && !deq.isEmpty()) {
            const toDeleteId = deq.pop();
            const toDelete = cacheMap.get(toDeleteId).unsafeCoerce();
            if (toDelete != null) {
                cacheSize -= toDelete.compressedByteLength;
                removedIds.push(toDeleteId);
            }
        }
        for (let i = removedIds.length - 1; i >= 0; --i) {
            const removedId = removedIds[i];
            const item = cacheMap.get(removedId).unsafeCoerce();
            if (cacheSize + item.compressedByteLength < maxSize) {
                deq.push(removedId);
                cacheSize += item.compressedByteLength;
            }
            else {
                state.cacheMap.delete(removedId);
            }
        }
        deq.unshift(key);
        cacheMap.set(key, compressed);
        state.cacheSize = cacheSize;
    }, 'addCodeToCache');
    const setLoader = moduleBuilder.commit((state, data) => {
        if (data.op === 'delete') {
            utils.vueDelete(state.loaders, data.key);
        }
        else {
            utils.vueSet(state.loaders, data.key, data.value);
        }
    }, 'setLoader');
    const getOrDownload = moduleBuilder.dispatch((ctx, { load, cacheKey, }) => ctx.state.cacheMap.get(cacheKey).caseOf({
        Just: async (cached) => {
            accessedCode({ key: cacheKey });
            return await cached.decompressPromise();
        },
        Nothing: () => {
            if (cacheKey in ctx.state.loaders) {
                return ctx.state.loaders[cacheKey];
            }
            let promise;
            /** Clear the loader for this file. */
            const clearLoader = () => {
                promise = undefined;
                setLoader({ op: 'delete', key: cacheKey });
            };
            promise = load()
                .then(data => compression
                .compressPromise(data)
                .then(compressed => {
                addCodeToCache({
                    key: cacheKey,
                    compressed,
                });
            })
                .then(() => data, () => data))
                .finally(() => {
                clearLoader();
            });
            setLoader({ op: 'set', key: cacheKey, value: promise });
            return promise;
        },
    }), 'getOrDownload');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    function loadCodeFromRoute({ route, hash = Maybe.empty(), }) {
        const cacheKey = makeURLCacheKey(route, hash);
        const fullUrl = utils.buildUrl(route, { query: { as_url: true } });
        return getOrDownload({
            cacheKey,
            load: async () => {
                const buf = await fileUtils.downloadRawFile(fullUrl);
                return new Uint8Array(buf);
            },
        });
    }
    CodeStore.loadCodeFromRoute = loadCodeFromRoute;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    function loadCode(payload) {
        if (payload.file == null) {
            return loadCodeFromRoute({
                route: ['api', 'v1', 'code', payload.fileId],
            });
        }
        else {
            const file = payload.file;
            return loadCodeFromRoute({
                route: ['api', 'v1', 'code', file.id],
                hash: file.hash,
            });
        }
    }
    CodeStore.loadCode = loadCode;
    /**
     * Download a file and decompress it from a signed S3 link.
     *
     * Compression detection is done as described in the @codegrade/compression module.
     *
     * @param url The URL to the file.
     * @returns The decompressed downloaded file.
     */
    function downloadCompressedFileFromS3(url) {
        return getOrDownload({
            cacheKey: makeURLCacheKey(url, Maybe.empty()),
            load: () => fileUtils.downloadCompressedFileFromS3(url),
        });
    }
    CodeStore.downloadCompressedFileFromS3 = downloadCompressedFileFromS3;
    /**
     * Download, decompress, and decode a JSON file from a signed S3 link.
     *
     * Compression detection is done as described in the @codegrade/compression module.
     *
     * @param url The URL to JSON.
     * @param parser The parser describing the format of the JSON.
     * @returns The decoded JSON.
     */
    async function downloadCompressedJSONFromS3(url, parser) {
        const response = await downloadCompressedFileFromS3(url);
        const json = JSON.parse(new TextDecoder('utf8', { fatal: true }).decode(response));
        const decodedEither = parser.decode(json);
        return await decodedEither.caseOf({
            Left: async (err) => {
                throw new Error(err);
            },
            Right: async (parsed) => parsed,
        });
    }
    CodeStore.downloadCompressedJSONFromS3 = downloadCompressedJSONFromS3;
    /**
     * Download a fixture in AutoTest v2 with the JWT and hash from the UploadedFile type.
     *
     * @param jwt The JWT of the UploadedFile.
     * @param hash The hash of the UploadedFile.
     * @returns A promise carrying a Notebook JSON.
     */
    async function downloadATv2Fixture(jwt, hash) {
        const cacheKey = makeJWTHashKey(jwt, Maybe.of(hash));
        const websocketClient = await eitherAsync.toPromise(getWsClient());
        const { cgResult: { url }, } = await eitherAsync.toPromise(websocketClient.sendRequest('getDownloadFixtureUrl', { jwt }));
        return await getOrDownload({
            cacheKey,
            load: () => fileUtils.downloadFileFromS3(url),
        });
    }
    CodeStore.downloadATv2Fixture = downloadATv2Fixture;
})(CodeStore || (CodeStore = {}));
